import * as THREE from 'three'
import { OrbitControls } from 'three-stdlib'
import { SceneRefs } from './types'

/**
 * Konfiguracja sceny Three.js
 */

const CAMERA_CONFIG = {
  FOV: 75,
  NEAR: 0.5, // Zwiększone z 0.1 — lepsza precyzja głębokości (mniej z-fighting)
  FAR: 200,  // Zmniejszone z 1000 — ogrodzenie nie potrzebuje 1km zasięgu
  DEFAULT_POSITION: { x: 0, y: 4, z: 6 }
}

const RENDERER_CONFIG = {
  ANTIALIAS: true,
  ALPHA: false,
  POWER_PREFERENCE: 'high-performance' as const,
  SHADOW_MAP_TYPE: THREE.PCFSoftShadowMap,
  TONE_MAPPING: THREE.NoToneMapping, // Bez tone mappingu — jak SketchUp, tekstury wyświetlają swoje prawdziwe kolory
  TONE_MAPPING_EXPOSURE: 1.0
}

const LIGHT_CONFIG = {
  AMBIENT: { color: 0xffffff, intensity: 0.7 },
  HEMISPHERE: { skyColor: 0xf7f9fb, groundColor: 0xe8e8e8, intensity: 0.5 },
  DIRECTIONAL: { color: 0xffffff, intensity: 0.7, position: [12, 18, 12] },
  FILL: { color: 0xf0f5ff, intensity: 0.45, position: [-12, 18, -12] },
  RIM: { color: 0xffffff, intensity: 0.2, position: [-8, 14, 12] },
  BOTTOM: { color: 0xffffff, intensity: 0.12, position: [0, -10, 0] }
}

const CONTROLS_CONFIG = {
  MIN_DISTANCE: 2.0,
  MAX_DISTANCE: Infinity,
  MIN_POLAR_ANGLE: 0,
  MAX_POLAR_ANGLE: Math.PI / 2,
  DAMPING_FACTOR: 0.05,
  ZOOM_SPEED: 0.5
}

/**
 * Inicjalizuje scenę Three.js z kamerą, rendererem i oświetleniem
 */
export function initializeScene(
  container: HTMLDivElement
): SceneRefs {
  // Sprawdź czy container ma wymiary
  if (container.clientWidth === 0 || container.clientHeight === 0) {
    console.warn('⚠️ Container ma wymiary 0x0, używam domyślnych wymiarów')
  }
  
  // ⚠️ USUNIĘTO: Sprawdzanie checkWebGLSupport() - może być zbyt restrykcyjne
  // Pozwólmy Three.js samemu obsłużyć błędy WebGL przez try-catch
  
  // Scena
  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0xe5e7eb)
  scene.fog = new THREE.Fog(0xe5e7eb, 50, 200)

  // Kamera
  const camera = new THREE.PerspectiveCamera(
    CAMERA_CONFIG.FOV,
    container.clientWidth / container.clientHeight,
    CAMERA_CONFIG.NEAR,
    CAMERA_CONFIG.FAR
  )
  camera.position.set(
    CAMERA_CONFIG.DEFAULT_POSITION.x,
    CAMERA_CONFIG.DEFAULT_POSITION.y,
    CAMERA_CONFIG.DEFAULT_POSITION.z
  )

  // Renderer - z obsługą błędów i fallback
  let renderer: THREE.WebGLRenderer | null = null
  let lastError: Error | null = null
  
  // Próba 1: Z pełną konfiguracją
  try {
    console.log('🔄 Próba utworzenia WebGL renderera z antialiasing...')
    renderer = new THREE.WebGLRenderer({
      antialias: RENDERER_CONFIG.ANTIALIAS,
      alpha: RENDERER_CONFIG.ALPHA,
      powerPreference: RENDERER_CONFIG.POWER_PREFERENCE,
      logarithmicDepthBuffer: true, // Eliminuje z-fighting (białe paski na krawędziach bloków)
      failIfMajorPerformanceCaveat: false
    })
    console.log('✅ WebGL renderer utworzony z antialiasing')
  } catch (error) {
    lastError = error instanceof Error ? error : new Error(String(error))
    console.error('❌ Błąd przy tworzeniu WebGL renderera z antialiasing:', lastError)
  }
  
  // Próba 2: Bez antialiasing
  if (!renderer) {
    try {
      console.log('🔄 Próba utworzenia WebGL renderera bez antialiasing (fallback 1)...')
      renderer = new THREE.WebGLRenderer({
        antialias: false,
        alpha: RENDERER_CONFIG.ALPHA,
        powerPreference: RENDERER_CONFIG.POWER_PREFERENCE,
        logarithmicDepthBuffer: true,
        failIfMajorPerformanceCaveat: false
      })
      console.warn('⚠️ WebGL renderer utworzony bez antialiasing (fallback 1)')
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
      console.error('❌ Błąd przy tworzeniu WebGL renderera (fallback 1):', lastError)
    }
  }
  
  // Próba 3: Minimalna konfiguracja
  if (!renderer) {
    try {
      console.log('🔄 Próba utworzenia WebGL renderera z minimalną konfiguracją (fallback 2)...')
      renderer = new THREE.WebGLRenderer({
        antialias: false,
        alpha: false,
        powerPreference: 'default' as const,
        logarithmicDepthBuffer: true,
        failIfMajorPerformanceCaveat: false
      })
      console.warn('⚠️ WebGL renderer utworzony z minimalną konfiguracją (fallback 2)')
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
      console.error('❌ Błąd przy tworzeniu WebGL renderera (fallback 2):', lastError)
    }
  }
  
  // Jeśli wszystkie próby się nie powiodły, rzuć błąd
  if (!renderer) {
    const errorMessage = lastError 
      ? `Nie można utworzyć WebGL renderera: ${lastError.message}` 
      : 'Nie można utworzyć WebGL renderera. Przeglądarka może nie wspierać WebGL.'
    console.error('❌ Wszystkie próby utworzenia WebGL renderera się nie powiodły')
    throw new Error(errorMessage)
  }
  
  // Sprawdź czy renderer ma poprawny kontekst WebGL (ale nie rzucaj błędu, jeśli nie ma - może działać)
  const context = renderer.getContext()
  if (!context) {
    console.warn('⚠️ WebGL context jest null, ale renderer został utworzony. Może działać mimo to.')
  } else {
    console.log('✅ WebGL context jest dostępny:', context instanceof WebGL2RenderingContext ? 'WebGL2' : 'WebGL1')
  }
  
  renderer.setSize(container.clientWidth, container.clientHeight)
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = RENDERER_CONFIG.SHADOW_MAP_TYPE
  renderer.toneMapping = RENDERER_CONFIG.TONE_MAPPING
  renderer.toneMappingExposure = RENDERER_CONFIG.TONE_MAPPING_EXPOSURE
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.shadowMap.autoUpdate = true
  container.appendChild(renderer.domElement)

  // 🎯 Environment mapping — jasna, neutralna scena dla odbić metalowych
  // (stary pmremGenerator.fromScene(new Scene()) tworzył CZARNY env map i ściemniał beton)
  // Teraz: jasna scena z neutralnym światłem → metal ma odbicia, beton nie jest dotknięty (Lambert)
  const pmremGenerator = new THREE.PMREMGenerator(renderer)
  pmremGenerator.compileEquirectangularShader()
  const envScene = new THREE.Scene()
  envScene.background = new THREE.Color(0xf5f5f5) // Jasne, neutralne tło
  const envLight = new THREE.AmbientLight(0xffffff, 1.0)
  envScene.add(envLight)
  const envMap = pmremGenerator.fromScene(envScene).texture
  scene.environment = envMap
  pmremGenerator.dispose()

  // Kontrolki kamery
  const controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = CONTROLS_CONFIG.DAMPING_FACTOR
  controls.minDistance = CONTROLS_CONFIG.MIN_DISTANCE
  controls.maxDistance = CONTROLS_CONFIG.MAX_DISTANCE
  controls.minPolarAngle = CONTROLS_CONFIG.MIN_POLAR_ANGLE
  controls.maxPolarAngle = CONTROLS_CONFIG.MAX_POLAR_ANGLE
  controls.zoomSpeed = CONTROLS_CONFIG.ZOOM_SPEED
  
  // 🎯 TOUCH EVENTS DLA MOBILE
  controls.touches = {
    ONE: THREE.TOUCH.PAN,
    TWO: THREE.TOUCH.DOLLY_PAN
  }

  // Oświetlenie
  setupLighting(scene)

  // Siatka pomocnicza - kwadraciki 0.396x0.396
  const gridHelper = new THREE.GridHelper(200, 505)
  scene.add(gridHelper)

  // Raycaster
  const raycaster = new THREE.Raycaster()
  const mouse = new THREE.Vector2()

  return {
    scene,
    renderer,
    camera,
    controls,
    raycaster,
    mouse
  }
}

/**
 * Konfiguruje oświetlenie sceny
 */
function setupLighting(scene: THREE.Scene): void {
  // Ambient light
  const ambientLight = new THREE.AmbientLight(
    LIGHT_CONFIG.AMBIENT.color,
    LIGHT_CONFIG.AMBIENT.intensity
  )
  scene.add(ambientLight)

  // Hemisphere light - miękkie światło z góry i odbite od podłoża
  const hemisphereLight = new THREE.HemisphereLight(
    LIGHT_CONFIG.HEMISPHERE.skyColor,
    LIGHT_CONFIG.HEMISPHERE.groundColor,
    LIGHT_CONFIG.HEMISPHERE.intensity
  )
  hemisphereLight.position.set(0, 30, 0)
  scene.add(hemisphereLight)

  // Directional light (główne światło - słońce)
  const directionalLight = new THREE.DirectionalLight(
    LIGHT_CONFIG.DIRECTIONAL.color,
    LIGHT_CONFIG.DIRECTIONAL.intensity
  )
  directionalLight.position.set(...LIGHT_CONFIG.DIRECTIONAL.position)
  directionalLight.castShadow = true
  directionalLight.shadow.mapSize.width = 2048
  directionalLight.shadow.mapSize.height = 2048
  directionalLight.shadow.camera.near = 0.5
  directionalLight.shadow.camera.far = 50
  directionalLight.shadow.camera.left = -25
  directionalLight.shadow.camera.right = 25
  directionalLight.shadow.camera.top = 25
  directionalLight.shadow.camera.bottom = -25
  // Zmniejsz intensywność cieni - rozjaśnij cienie
  directionalLight.shadow.bias = -0.0001 // Zmniejsz bias dla jaśniejszych cieni
  directionalLight.shadow.radius = 4 // Zwiększ radius dla miękkich, jaśniejszych cieni
  scene.add(directionalLight)

  // Fill light
  const fillLight = new THREE.DirectionalLight(
    LIGHT_CONFIG.FILL.color,
    LIGHT_CONFIG.FILL.intensity
  )
  fillLight.position.set(...LIGHT_CONFIG.FILL.position)
  scene.add(fillLight)

  // Rim light - podbija krawędzie z drugiej strony, by uniknąć ciemnych fasad
  const rimLight = new THREE.DirectionalLight(
    LIGHT_CONFIG.RIM.color,
    LIGHT_CONFIG.RIM.intensity
  )
  rimLight.position.set(...LIGHT_CONFIG.RIM.position)
  scene.add(rimLight)

  // Bottom light
  const bottomLight = new THREE.DirectionalLight(
    LIGHT_CONFIG.BOTTOM.color,
    LIGHT_CONFIG.BOTTOM.intensity
  )
  bottomLight.position.set(...LIGHT_CONFIG.BOTTOM.position)
  scene.add(bottomLight)
}

/**
 * Resetuje kamerę do pozycji domyślnej
 */
export function resetCamera(refs: SceneRefs): void {
  if (refs.camera && refs.controls) {
    refs.camera.position.set(
      CAMERA_CONFIG.DEFAULT_POSITION.x,
      CAMERA_CONFIG.DEFAULT_POSITION.y,
      CAMERA_CONFIG.DEFAULT_POSITION.z
    )
    refs.controls.target.set(0, 0, 0)
    refs.controls.update()
  }
}

/**
 * Zoom do wszystkich elementów w scenie
 */
export function zoomToFit(refs: SceneRefs): void {
  if (!refs.camera || !refs.controls || !refs.scene) return

  // 🎯 Zbierz tylko elementy ogrodzenia (pomiń pomocnicze obiekty jak GridHelper, światła)
  const fenceElementGroups: THREE.Object3D[] = []
  
  refs.scene.traverse((obj: any) => {
    // Sprawdź czy to grupa elementu ogrodzenia
    if (obj.name === 'post_group' || 
        obj.name === 'section_group' || 
        obj.name === 'gate_group' || 
        obj.name === 'door_group') {
      fenceElementGroups.push(obj)
    }
  })

  // 🎯 Jeśli nie ma elementów ogrodzenia, użyj domyślnego widoku
  if (fenceElementGroups.length === 0) {
    resetCamera(refs)
    return
  }

  // 🎯 Oblicz bounding box tylko dla elementów ogrodzenia
  const box = new THREE.Box3()
  fenceElementGroups.forEach(group => {
    box.expandByObject(group)
  })

  // 🎯 Sprawdź czy bounding box jest ważny
  if (!box.isEmpty()) {
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())

    // 🎯 Użyj tylko wymiarów X i Z (poziome), ignoruj Y (wysokość)
    const maxDim = Math.max(size.x, size.z)
    
    // 🎯 Jeśli nie ma wymiarów, użyj domyślnego widoku
    if (maxDim === 0) {
      resetCamera(refs)
      return
    }

    // 🎯 Obliczenie odległości kamery
    const fov = refs.camera.fov * (Math.PI / 180)
    const aspect = refs.camera.aspect || 1
    
    // 🎯 Oblicz poziomy FOV
    const horizontalFov = 2 * Math.atan(Math.tan(fov / 2) * aspect)
    
    // 🎯 Oblicz odległość potrzebną do zobaczenia elementów w poziomie
    const distanceNeeded = maxDim / (2 * Math.tan(horizontalFov / 2))
    
    // 🎯 Dla przybliżenia zmniejsz odległość (użyj 0.5 dla bliższego widoku)
    let distance = distanceNeeded * 0.5
    
    // 🎯 Upewnij się, że odległość nie jest zbyt mała
    distance = Math.max(distance, CONTROLS_CONFIG.MIN_DISTANCE * 1.0)

    // 🎯 Zachowaj obecny kierunek poziomy kamery (azymut), ale dostosuj tylko odległość i wysokość
    const currentPos = refs.camera.position.clone()
    const currentTarget = refs.controls.target.clone()
    
    // 🎯 Oblicz obecny kierunek poziomy (azymut) - tylko X i Z
    const currentDirX = currentTarget.x - currentPos.x
    const currentDirZ = currentTarget.z - currentPos.z
    const currentHorizontalDist = Math.sqrt(currentDirX * currentDirX + currentDirZ * currentDirZ)
    
    // 🎯 Jeśli obecna pozycja jest zbyt blisko centrum lub z góry, użyj domyślnego kierunku
    let horizontalDirX: number, horizontalDirZ: number
    if (currentHorizontalDist < 0.1) {
      // 🎯 Użyj domyślnego kierunku (patrzenie od z=6, czyli w stronę -Z)
      horizontalDirX = 0
      horizontalDirZ = -1 // Patrzymy w stronę ujemnego Z
    } else {
      // 🎯 Zachowaj obecny kierunek poziomy (znormalizowany)
      const norm = 1 / currentHorizontalDist
      horizontalDirX = currentDirX * norm
      horizontalDirZ = currentDirZ * norm
    }
    
    // 🎯 Oblicz wysokość kamery (zachowaj podobny kąt jak domyślna pozycja)
    const heightRatio = 4 / 6 // Proporcja z domyślnej pozycji (y=4, z=6)
    const cameraHeight = center.y + distance * heightRatio + Math.max(size.y * 0.3, 1.5)
    
    // 🎯 Ustaw pozycję kamery zachowując obecny kierunek poziomy
    refs.camera.position.set(
      center.x - horizontalDirX * distance,
      cameraHeight,
      center.z - horizontalDirZ * distance
    )
    
    // 🎯 Ustaw cel kamery na środek elementów
    refs.controls.target.copy(center)
    refs.controls.update()
  } else {
    // 🎯 Fallback: zresetuj kamerę
    resetCamera(refs)
  }
}

/**
 * Obsługuje resize okna
 */
export function handleResize(
  container: HTMLDivElement | null,
  refs: SceneRefs
): void {
  if (!refs.camera || !refs.renderer || !container) return

  const width = container.clientWidth
  const height = container.clientHeight

  refs.camera.aspect = width / height
  refs.camera.updateProjectionMatrix()
  refs.renderer.setSize(width, height)
}

/**
 * Cleanup zasobów Three.js
 */
export function disposeScene(refs: SceneRefs): void {
  if (refs.renderer) {
    refs.renderer.dispose()
  }

  if (refs.scene) {
    refs.scene.traverse((obj: any) => {
      if (obj.geometry) obj.geometry.dispose()
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach(mat => mat.dispose())
        } else {
          obj.material.dispose()
        }
      }
    })
  }
}










